// Helper function to create config for each integration
const createConfig = (name, clientId, authority, scope, params = {}) => ({
  name,
  clientId,
  authority,
  redirectUri: `${import.meta.env.VITE_BASE_URL}/integrations/${name == "google" ? "gmail" : name}/callback`,
  scope,
  response_type: "code",
  response_mode: "query",
  access_type: "offline", // Default offline access for all integrations
  ...params
})

// Common Google Attributes (apply to both Gmail and Calendar)
const googleCommonAttrs = {
  gsiwebsdk: "3",
  prompt: "select_account consent",
  include_granted_scopes: "true", // Avoid re-prompting for already granted scopes
  enable_granular_consent: "true", // Show granular consent for each requested scope
  service: "lso",
  o2v: "2",
  ddm: "1",
  access_type: "offline",
  flowName: "GeneralOAuthFlow"
}

// Common Google Scopes
const commonGoogleScopes = ["openid", "https://www.googleapis.com/auth/userinfo.email", "https://www.googleapis.com/auth/userinfo.profile"]

const allGoogleScopes = [
  "openid",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/contacts.readonly",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/calendar",
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/spreadsheets"
]

// Gmail Scopes
const gmailScopes = [...commonGoogleScopes, "https://www.googleapis.com/auth/gmail.modify"]

// Google Calendar Scopes
const googleCalendarScopes = [
  ...commonGoogleScopes,
  "https://www.googleapis.com/auth/calendar",
  "https://www.googleapis.com/auth/calendar.events"
]

// Gmail Config
const gmailConfig = createConfig(
  "gmail",
  import.meta.env.VITE_GOOGLE_CLIENT_ID,
  "https://accounts.google.com/o/oauth2/v2/auth",
  gmailScopes.join(" "),
  googleCommonAttrs
)

// Full Google Config
const fullGoogleConfig = createConfig(
  "google",
  import.meta.env.VITE_GOOGLE_CLIENT_ID,
  "https://accounts.google.com/o/oauth2/v2/auth",
  allGoogleScopes.join(" "),
  googleCommonAttrs
)

// Google Calendar Config
const googleCalendarConfig = createConfig(
  "google-calendar",
  import.meta.env.VITE_GOOGLE_CLIENT_ID,
  "https://accounts.google.com/o/oauth2/v2/auth",
  googleCalendarScopes.join(" "),
  googleCommonAttrs
)

// Combined Google (Gmail + Calendar) Scopes
const googleConfig = createConfig(
  "google",
  import.meta.env.VITE_GOOGLE_CLIENT_ID,
  "https://accounts.google.com/o/oauth2/v2/auth",
  [...gmailScopes, ...googleCalendarScopes].join(" "),
  googleCommonAttrs
)

// Azure Config
const azureConfig = createConfig(
  "outlook",
  import.meta.env.VITE_AZURE_CLIENT_ID,
  "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
  [
    "Calendars.Read",
    "Calendars.Read.Shared",
    "Calendars.ReadWrite",
    "Calendars.ReadWrite.Shared",
    "Contacts.Read",
    "Contacts.Read.Shared",
    "Contacts.ReadWrite",
    "Contacts.ReadWrite.Shared",
    "email",
    "Mail.Read",
    "Mail.ReadWrite",
    "Mail.Send",
    "offline_access",
    "openid",
    "profile",
    "User.Read"
  ].join(" "),
  {
    prompt: "select_account",
    code_challenge_method: "S256",
    access_type: "offline"
  }
)

// HubSpot Config
const hubspotConfig = createConfig(
  "hubspot",
  import.meta.env.VITE_HUBSPOT_CLIENT_ID,
  "https://app.hubspot.com/oauth/authorize",
  import.meta.env.VITE_HUBSPOT_SCOPES,
  { access_type: "offline" }
)

// Salesforce Config
const salesforceConfig = createConfig(
  "salesforce",
  import.meta.env.VITE_SALESFORCE_CLIENT_ID,
  "https://login.salesforce.com/services/oauth2/authorize",
  undefined,
  { access_type: "offline" }
)

// Slack Config
const slackConfig = createConfig(
  "slack",
  import.meta.env.VITE_SLACK_CLIENT_ID,
  "https://slack.com/oauth/v2/authorize",
  import.meta.env.VITE_SLACK_SLACK_SCOPES,
  {
    user_scope: import.meta.env.VITE_SLACK_SLACK_USER_SCOPES,
    access_type: "offline"
  }
)

// WhatsApp Scopes
const whatsappScopes = "email public_profile business_management whatsapp_business_messaging whatsapp_business_management"

// WhatsApp Config
const whatsappConfig = createConfig(
  "whatsapp",
  import.meta.env.VITE_META_CLIENT_ID,
  "https://www.facebook.com/v21.0/dialog/oauth",
  whatsappScopes,
  {
    prompt: "consent",
    response_mode: "query",
    access_type: "offline"
  }
)

// Pipedrive Config
const pipedriveConfig = createConfig(
  "pipedrive",
  import.meta.env.VITE_PIPEDRIVE_CLIENT_ID,
  "https://oauth.pipedrive.com/oauth/authorize",
  undefined,
  { access_type: "offline" }
)

// Store all configs in a single object for easy access by name
const configs = {
  outlook: azureConfig,
  google: googleConfig,
  gmail: gmailConfig,
  "full-google": fullGoogleConfig,
  "google-calendar": googleCalendarConfig,
  hubspot: hubspotConfig,
  salesforce: salesforceConfig,
  slack: slackConfig,
  whatsapp: whatsappConfig,
  pipedrive: pipedriveConfig
}

// Main function to generate the auth URL based on the config and options
async function getAuthUrl(
  name,
  {
    service = null,
    scope = null,
    redirect_uri_name = null,
    state = null,
    only_user_scopes = false,
    use_sandbox = false,
    is_new_wa_signup = false,
    extra_params = {}
  } = {}
) {
  let config = configs[name]

  if (!config) {
    throw new Error(`Unknown configuration for ${name}`)
  }

  let url = new URL(config.authority)

  // Handle sandbox for Salesforce
  if (use_sandbox && name === "salesforce") {
    url = new URL(config.authority.replace("login.salesforce.com", "test.salesforce.com"))
  }

  // Determine redirect URI based on the integration
  let redirectUriBase =
    name === "slack" || name === "whatsapp" ? import.meta.env.VITE_BASE_URL.replace("http://", "https://") : import.meta.env.VITE_BASE_URL

  let redirectUri = `${redirectUriBase}/integrations/${redirect_uri_name || service || name}/callback`

  // Append core OAuth parameters
  url.searchParams.append("redirect_uri", redirectUri)
  url.searchParams.append("client_id", config.clientId)
  url.searchParams.append("response_type", config.response_type)
  url.searchParams.append("response_mode", config.response_mode)
  url.searchParams.append("access_type", "offline") // Ensure offline access in URL params

  // Add WhatsApp specific config
  if (is_new_wa_signup && name === "whatsapp") {
    url.searchParams.append("config_id", "932453642271578")
  }

  // Handle scopes
  if (scope) {
    config.scope = scope
  }
  if (config.scope) {
    url.searchParams.append("scope", config.scope)
  }

  // Optional state parameter
  if (state) {
    url.searchParams.append("state", state)
  }

  // PKCE: Handle code challenge and code verifier for S256
  if (config.code_challenge_method && config.code_challenge_method === "S256") {
    const codeVerifier = [...Array(128)].map(() => Math.random().toString(36)[2]).join("")
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    localStorage.setItem("code_verifier", codeVerifier)
    localStorage.setItem("scope", config.scope)
    url.searchParams.append("code_challenge", codeChallenge)
    url.searchParams.append("code_challenge_method", config.code_challenge_method)
  }

  // Prompt handling
  if (config.prompt) {
    url.searchParams.append("prompt", config.prompt)
  }

  // Handle user-specific scopes for Slack
  if (config.user_scope && only_user_scopes) {
    url.searchParams.append("user_scope", config.user_scope)
    url.searchParams.delete("scope")
  }

  // Append extra parameters for Google services
  if (name === "google" || name === "gmail" || name === "google-calendar" || name === "full-google") {
    url.searchParams.append("service", config.service)
    url.searchParams.append("o2v", config.o2v)
    url.searchParams.append("ddm", config.ddm)
    url.searchParams.append("flowName", config.flowName)
    url.searchParams.append("gsiwebsdk", config.gsiwebsdk)
    url.searchParams.append("include_granted_scopes", config.include_granted_scopes)
    url.searchParams.append("enable_granular_consent", config.enable_granular_consent)
  }

  // Add any extra parameters
  if (extra_params) {
    for (const [key, value] of Object.entries(extra_params)) {
      url.searchParams.append(key, value)
    }
  }

  return url.toString()
}

// PKCE helper function for generating a code challenge
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest("SHA-256", data)
  const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
  return base64Digest.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

// MSAL Config
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: `${window.location.origin}/integrations/outlook/callback`,
    autoRefreshToken: true,
    responseMode: "query",
    prompt: "no_session",
    scopes: [
      "Calendars.Read",
      "Calendars.Read.Shared",
      "Calendars.ReadWrite",
      "Calendars.ReadWrite.Shared",
      "Contacts.Read",
      "Contacts.Read.Shared",
      "Contacts.ReadWrite",
      "Contacts.ReadWrite.Shared",
      "email",
      "Mail.Read",
      "Mail.ReadWrite",
      "Mail.Send",
      "offline_access",
      "openid",
      "profile",
      "User.Read"
    ]
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true
  }
}

// Base Google Config
const googleBaseConfig = {
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
  scope: "email profile",
  access_type: "offline"
}

// Export configurations
export {
  getAuthUrl,
  azureConfig,
  googleConfig,
  hubspotConfig,
  salesforceConfig,
  slackConfig,
  whatsappConfig,
  msalConfig,
  googleBaseConfig,
  pipedriveConfig
}
